import dayjs, { Dayjs } from 'dayjs';
import { Transform, Type } from 'class-transformer';
import { Region, Zone } from './orderTypes';

export interface BundleMeasures {
  distanceMeters: number;
  durationSeconds: number;
  drivingTimeSeconds: number;
  waitingTimeSeconds: number;
  timePerStopSeconds?: number;
}

export class Instruction {
  orderId: string;
  type: InstructionType;
  completed: boolean;

  @Type(() => Date)
  @Transform(value => dayjs(value))
  eta?: Dayjs;

  constructor(
    orderId: string,
    type: InstructionType,
    completed: boolean,
    address: string,
    eta?: Dayjs
  ) {
    this.orderId = orderId;
    this.type = type;
    this.completed = completed;
    this.eta = eta;
  }
}

export class BundleJson {
  id: string;
  allInstructionsComplete: boolean;
  assignedCourierId?: string;
  assignedCourierName?: string;
  assignedCourierPhoneNumber?: string;
  assignedCourierPhoneCode?: string;
  assignedCourierPredictionModel: string;
  travelMode?: TravelMode;
  carType?: CarType;
  created: string;
  @Type(() => Instruction)
  instructions: Instruction[];
  shelfId?: string;
  status: BundleStatus;
  xxl?: boolean;
  measures?: BundleMeasures;
  region: Region;
  versionUuid: string;
}

export class BundlesPageJson {
  @Type(() => BundleJson)
  items: BundleJson[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export enum InstructionType {
  Delivery = 'DELIVERY',
  Pickup = 'PICKUP',
}

export enum BundleStatus {
  Accepted = 'ACCEPTED',
  Completed = 'COMPLETED',
  Deleted = 'DELETED',
  Draft = 'DRAFT',
  Proposed = 'PROPOSED',
  Published = 'PUBLISHED',
}

export const BundleStatusActive = [
  BundleStatus.Draft,
  BundleStatus.Published,
  BundleStatus.Proposed,
  BundleStatus.Accepted,
];

export enum CourierStatus {
  Offline = 'offline',
  Inactive = 'inactive',
  Available = 'available',
  Unavailable = 'unavailable',
}

export enum TravelMode {
  NONE = '',
  CAR = 'CAR',
  BIKE = 'BIKE',
}

export enum CourierTravelModeString {
  NONE = 'Transit',
  CAR = 'Car',
  BIKE = 'Bike',
}

export enum CarType {
  PETROL = 'PETROL',
  ELECTRIC = 'ELECTRIC',
  DIESEL = 'DIESEL',
  HYBRID = 'HYBRID',
  CARGO_BIKE = 'CARGO_BIKE',
  NORMAL_BIKE = 'NORMAL_BIKE',
  null = 'null', // required some places, so to make it not break..
}

export interface CourierJson {
  email: string;
  firstName: string;
  fullName: string;
  id: number;
  lastName?: string;
  latitude: number;
  isLocked: boolean;
  longitude: number;
  phoneCode: string;
  phoneNumber: string;
  rating?: number;
  status: CourierStatus;
  isCourier: boolean;
  isSorter: boolean;
  travelMode: TravelMode;
  carType?: CarType;
  plateNumbers?: string[];
  xxl: boolean;
  companyName?: string;
  companyNumber?: string;
  etaOffsetSeconds: number;
  regions: Region[];
  locationUpdatedAt?: Date;
  preferredGeoZones?: string[];
  notes?: string;
  predictionModel: string;
  shiftsCount: number;
  dateCreated: Date;
}

export interface BundleStats {
  bundleId: string;
  created: string;
  region: Region;
  courierId: number;
  courierName: string;
  travelMode: string;
  carType: string;
  measures: BundleMeasures;
  orderCount: number;
  firstDeliveryEta?: string;
  lastDeliveryEta?: string;
  firstDeliveryTime: string;
  lastDeliveryTime: string;
  zones: Zone[];
  deliveryCount: number;
}

export interface CourierRatingJson {
  courierId: string;
  averageRating: number;
}

export interface CourierRetentionJson {
  courierId: number;
  numberOfTimesBooked: number;
}

export enum BundleSource {
  MANUAL = 'MANUAL',
  AUTO_BUNDLER = 'AUTO_BUNDLER',
}

export interface BundleRouteStep {
  latitude: number;
  longitude: number;
  windowStart: string;
  windowEnd: string;
  orderId: string;
  address: string;
  eta?: string;
  deliveredAt?: string;
}

export interface BundleRoute {
  id: string;
  source: BundleSource;
  date: string;
  timePerStopSeconds: number;
  created: string;
  steps: BundleRouteStep[];
}

export interface AggregateValues {
  count: number;
  average: number;
  minimum: number;
  maximum: number;
  stdDev: number;
}

export interface BundleRoutesMetrics {
  timePerStopSeconds: AggregateValues;
  ordersPerBundle: AggregateValues;
  ordersWithEtaOutsideWindowCount: number;
  avgEtaOutsideWindowSeconds: number;
  ordersWithDeliveryOutsideWindowCount: number;
  avgDeliveryOutsideWindowSeconds: number;
}

export interface BundleRoutes {
  id: string;
  date: string;
  region: Region;
  source: BundleSource;
  timePerStopSeconds: number;
  routes: BundleRoute[];
  metrics: BundleRoutesMetrics;
  bundlesCount: number;
  ordersCount: number;
}

export interface BundleRoutesResult {
  routeSets: BundleRoutes[];
}

export interface BundleRoutesSummary {
  id: string;
  date: string;
  region: Region;
  source: BundleSource;
  bundlesCount: number;
  ordersCount: number;
}

export interface BundleRoutesSummaryResult {
  routeSets: BundleRoutesSummary[];
}

export enum ReorderMode {
  AutoBundler = 'AUTO_BUNDLER',
  OpenRoute = 'OPENROUTE',
}

export enum PredictionModel {
  NeuralNetwork = 'NEURAL_NETWORK',
  DecisionTree = 'DECISION_TREE',
  NeuralNetworkOSRM = 'NEURAL_NETWORK_OSRM',
  DecisionTreeOSRM = 'DECISION_TREE_OSRM',
}

export interface BundleForRelabelling {
  bundleId: string;
  shelfId: string;
  region: Region;
  orderIdsToRelabel: string[];
}
